// Traveler-friendly copy for ShellError values from the native shell plugin.
// Keep the mapping in one place so the Diagnostics row and any future surfaces
// (toasts, banners) stay consistent.
import type { ShellError } from "./shell-mode";

export type HumanShellError = {
  label: string;
  hint?: string;
};

function nsurlCode(detail: string | null | undefined): number | null {
  if (!detail) return null;
  const m = String(detail).match(/code\s*=\s*(-?\d+)/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

export function humanizeShellError(e: ShellError): HumanShellError {
  const code = nsurlCode(e.detail);

  switch (e.reason) {
    case "probe_failed": {
      // Common iOS NSURLErrorDomain codes — see Apple's NSURLError constants.
      if (code === -1009) {
        return {
          label: "No internet right now",
          hint: "Roava is running from the copy saved on your device.",
        };
      }
      if (code === -1001) {
        return {
          label: "Connection too slow to reach Roava",
          hint: "We'll keep trying in the background.",
        };
      }
      if (code === -1003 || code === -1004) {
        return {
          label: "Couldn't reach Roava's server",
          hint: "We'll retry when the signal improves.",
        };
      }
      return {
        label: "Couldn't reach Roava",
        hint: "We'll retry automatically.",
      };
    }
    case "probe_timeout":
      return {
        label: "Internet check timed out",
        hint: "We'll keep trying in the background.",
      };
    case "nav_provisional":
    case "nav_committed":
      return {
        label: "Connection dropped while loading",
        hint: "Switched to the copy saved on your device.",
      };
    case "safety_timeout":
      return {
        label: "Roava took too long to load",
        hint: "Switched to the copy saved on your device.",
      };
    case "bundled_fallback":
      return {
        label: "Using the copy saved on your device",
        hint: "Will switch back when internet returns.",
      };
    case "remote_success_cleared":
      return { label: "Back online" };
    case "unknown":
    default:
      return {
        label: "Connection problem",
        hint: "We'll retry automatically.",
      };
  }
}
